import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/utils/api";
import { toast } from "sonner";
import { Wallet, ArrowLeft, Loader2, CheckCircle2, Search, Plus, MinusCircle } from "lucide-react";

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000];

export default function AdminWalletTopup() {
  const navigate = useNavigate();
  const [custQ, setCustQ] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [custOptions, setCustOptions] = useState([]);
  const [customer, setCustomer] = useState(null);

  const [summary, setSummary] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("CASHFREE"); // CASHFREE | MANUAL_CREDIT
  const [result, setResult] = useState(null);

  // Customer typeahead
  useEffect(() => {
    if (!custOpen) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(
          `/v2/customer-profiles/search?q=${encodeURIComponent(custQ)}`
        );
        setCustOptions(r.data?.results || []);
      } catch {
        setCustOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [custQ, custOpen]);

  const loadSummary = async (id) => {
    try {
      const r = await api.get(`/v2/wallets/${id}/summary`);
      setSummary(r.data);
    } catch {
      setSummary(null);
    }
  };

  const selectCustomer = async (c) => {
    setCustomer(c);
    setCustOpen(false);
    setCustQ(c.company_name || c.contact_person || "");
    await loadSummary(c.customer_id);
  };

  const submit = async () => {
    if (!customer) {
      toast.error("Select a customer first");
      return;
    }
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      if (mode === "MANUAL_CREDIT") {
        if (!reason.trim()) {
          toast.error("Reason is required for manual credit");
          setBusy(false);
          return;
        }
        const r = await api.post(`/v2/wallets/${customer.customer_id}/manual-credit`, {
          amount: amt,
          reason,
          reference,
        });
        setResult({ status: "SUCCESS", amount: amt, newBalance: r.data?.new_balance });
        toast.success("Manual credit posted");
        loadSummary(customer.customer_id);
      } else {
        // Cashfree mock path (admin acts on behalf of customer)
        if (amt < 100) {
          toast.error("Cashfree top-up min ₹100");
          setBusy(false);
          return;
        }
        const init = await api.post(`/v2/payments/wallet/initiate`, {
          customer_id: customer.customer_id,
          amount: amt,
        });
        const orderId = init.data?.cashfree_order_id;
        const verR = await api.post(`/v2/payments/wallet/verify`, {
          cashfree_order_id: orderId,
        });
        setResult({
          status: verR.data?.status || "SUCCESS",
          amount: amt,
          newBalance: verR.data?.new_balance,
        });
        loadSummary(customer.customer_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const manualDebit = async () => {
    const amt = Number(amount);
    if (!customer || !amt || !reason.trim()) {
      toast.error("Customer, amount and reason required");
      return;
    }
    if (!window.confirm(`Debit ₹${amt} from ${customer.company_name}?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/v2/wallets/${customer.customer_id}/manual-debit`, {
        amount: amt,
        reason,
        reference,
      });
      setResult({ status: "SUCCESS", amount: amt, newBalance: r.data?.new_balance });
      toast.success("Manual debit posted");
      loadSummary(customer.customer_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-3xl mx-auto" data-testid="admin-wallet-topup">
        <button
          onClick={() => navigate("/admin/wallet-dashboard")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="w-4 h-4" /> Wallet Dashboard
        </button>

        <div>
          <h1 className="text-2xl font-bold text-slate-800">Wallet Top-up (Admin)</h1>
          <p className="text-sm text-slate-500">
            Recharge customer wallets via Cashfree, or post manual credits/debits
          </p>
        </div>

        {/* Customer selector */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <label className="text-xs font-bold uppercase text-slate-500">Customer</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={custQ}
              onFocus={() => setCustOpen(true)}
              onChange={(e) => {
                setCustQ(e.target.value);
                setCustOpen(true);
                setCustomer(null);
              }}
              placeholder="Search by name, email, mobile, GSTIN…"
              className="pl-8"
              data-testid="customer-search-input"
            />
            {custOpen && custOptions.length > 0 && (
              <div className="absolute top-11 left-0 right-0 max-h-64 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg z-10">
                {custOptions.map((c) => (
                  <button
                    key={c.customer_id}
                    onClick={() => selectCustomer(c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-b-0"
                    data-testid={`cust-option-${c.customer_id}`}
                  >
                    <div className="font-semibold">{c.company_name || c.contact_person}</div>
                    <div className="text-xs text-slate-500">
                      {c.email} {c.mobile && `· ${c.mobile}`}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {summary && (
            <div className="grid grid-cols-3 gap-2 mt-2">
              <Stat label="Balance" value={`₹${Number(summary.current_balance || 0).toFixed(2)}`} />
              <Stat label="Type" value={summary.wallet_type || "PREPAID"} />
              <Stat
                label="Credit Limit"
                value={summary.credit_limit ? `₹${Number(summary.credit_limit).toFixed(0)}` : "—"}
              />
            </div>
          )}
        </div>

        {customer && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <div className="flex gap-2">
              <button
                onClick={() => setMode("CASHFREE")}
                className={`flex-1 py-2 rounded border text-sm font-semibold ${
                  mode === "CASHFREE"
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-600 border-slate-300"
                }`}
                data-testid="mode-cashfree"
              >
                Cashfree Top-up
              </button>
              <button
                onClick={() => setMode("MANUAL_CREDIT")}
                className={`flex-1 py-2 rounded border text-sm font-semibold ${
                  mode === "MANUAL_CREDIT"
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-600 border-slate-300"
                }`}
                data-testid="mode-manual"
              >
                Manual Credit / Debit
              </button>
            </div>

            <div>
              <div className="text-xs font-bold uppercase text-slate-500 mb-2">Amount</div>
              <div className="grid grid-cols-5 gap-2 mb-2">
                {QUICK_AMOUNTS.map((a) => (
                  <button
                    key={a}
                    onClick={() => setAmount(String(a))}
                    className={`border rounded-md py-2 text-sm font-semibold transition ${
                      Number(amount) === a
                        ? "bg-slate-800 text-white border-slate-800"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    ₹{a}
                  </button>
                ))}
              </div>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Custom amount"
                data-testid="amount-input"
              />
            </div>

            {mode === "MANUAL_CREDIT" && (
              <>
                <div>
                  <label className="text-xs font-bold uppercase text-slate-500">Reason*</label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is this credit/debit being posted?"
                    rows={2}
                    data-testid="reason-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-slate-500">Reference</label>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="UTR / Internal reference"
                    data-testid="reference-input"
                  />
                </div>
              </>
            )}

            <div className="flex gap-2">
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={submit}
                disabled={busy}
                data-testid="submit-topup-btn"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                {mode === "CASHFREE" ? "Top-up via Cashfree" : "Post Credit"}
              </Button>
              {mode === "MANUAL_CREDIT" && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={manualDebit}
                  disabled={busy}
                  data-testid="manual-debit-btn"
                >
                  <MinusCircle className="w-4 h-4 mr-1" /> Debit Instead
                </Button>
              )}
            </div>
          </div>
        )}

        {result?.status === "SUCCESS" && (
          <div
            className="bg-emerald-50 border border-emerald-300 rounded-xl p-5 text-center"
            data-testid="topup-result"
          >
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
            <h3 className="text-lg font-bold text-emerald-800">
              ₹{Number(result.amount).toFixed(2)} processed
            </h3>
            {result.newBalance != null && (
              <p className="text-slate-700 mt-1 text-sm">
                New balance:{" "}
                <span className="font-bold">₹{Number(result.newBalance).toFixed(2)}</span>
              </p>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-2 text-center">
      <div className="text-[10px] uppercase text-slate-500 font-bold">{label}</div>
      <div className="text-sm font-bold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
